import { IApplicationFull } from '../types';
import { isNumber } from './format';

export const SHORT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  timeZone: 'UTC',
}; // apr 2024
export const MEDIUM_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
}; // april 2024
export const FULL_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}; // 23 apr 2024
export const NUMERIC_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  year: 'numeric',
  month: 'numeric',
}; // 04/2024
export const NUMERIC_WITH_TIMEZONE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  year: 'numeric',
  month: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
}; // 17/07/2024, 02:12 CEST

const NOT_DEFINED_DATE = 'N/D';

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const MONTH = DAY * 30;
const YEAR = DAY * 365;

export const getHumanReadableMonth = (month: number): string => ('0' + month).slice(-2);

export const transformDate = (rawDate: string | undefined): string => {
  const date = new Date(rawDate ? rawDate : new Date().toUTCString());

  const month: number = date.getUTCMonth();
  const year: number = date.getUTCFullYear();

  const humanReadableMonth: string = getHumanReadableMonth(month + 1);

  return `${year}-${humanReadableMonth}-01`;
};

export const getQuarter = (date: Date) => {
  return Math.floor((date.getMonth() + 3) / 3);
};

const isValidDate = (rawDate: string): boolean => {
  return isNumber(String(new Date(rawDate).getTime()));
};

const displayDateTime = (date: Date | null, options: Intl.DateTimeFormatOptions, timeZone?: string): string => {
  if (!date || isNaN(date.getTime())) {
    return NOT_DEFINED_DATE;
  }
  if (timeZone) {
    options = { ...options, timeZone };
  }
  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

export const getFormattedDate = (date: string | Date | undefined | null, timeZone = 'UTC') => {
  const currentDate = date ? new Date(date) : null;

  return {
    short: () => displayDateTime(currentDate, SHORT_DATE_FORMAT, timeZone),
    medium: () => displayDateTime(currentDate, MEDIUM_DATE_FORMAT, timeZone),
    full: () => displayDateTime(currentDate, FULL_DATE_FORMAT, timeZone),
    toISOString: () => (!currentDate ? NOT_DEFINED_DATE : currentDate.toISOString()),
    date: () => (!currentDate ? NOT_DEFINED_DATE : currentDate),
    fromNow: () => fromNow(!currentDate ? NOT_DEFINED_DATE : currentDate),
    numeric: () =>
      new Intl.DateTimeFormat('en-GB', NUMERIC_DATE_FORMAT).format(currentDate ?? undefined).replace(/ /g, '-'),
    numericWithTimezone: () => displayDateTime(currentDate, NUMERIC_WITH_TIMEZONE_DATE_FORMAT, timeZone),
  };
};

export const getFunctionnalDate = (date: Date | string | undefined) => {
  const currentDate = date ? new Date(date) : new Date();

  return {
    short: () => new Intl.DateTimeFormat('en-GB', SHORT_DATE_FORMAT).format(currentDate).replace(/ /g, '-'),
    medium: () => new Intl.DateTimeFormat('en-GB', MEDIUM_DATE_FORMAT).format(currentDate).replace(/ /g, '-'),
    full: () => new Intl.DateTimeFormat('en-GB', FULL_DATE_FORMAT).format(currentDate).replace(/ /g, '-'),
    numeric: () => new Intl.DateTimeFormat('en-GB', NUMERIC_DATE_FORMAT).format(currentDate).replace(/ /g, '-'),
  };
};

export const transformISODate = (rawDate: string | undefined): string => {
  const date = rawDate && isValidDate(rawDate) ? new Date(rawDate) : new Date();

  return new Intl.DateTimeFormat('en-GB', SHORT_DATE_FORMAT).format(date).replace(/ /g, '-').toUpperCase();
};

export const transformISOLongDate = (rawDate: string | null | undefined, firstDayOfMont = false): string => {
  let date = rawDate && isValidDate(rawDate) ? new Date(rawDate) : new Date();
  if (firstDayOfMont) {
    date = new Date(date.getFullYear(), date.getMonth(), 1);
  }
  const offset = date.getTimezoneOffset();
  const newDate = new Date(date.getTime() - offset * 60 * 1000);

  return newDate.toISOString().split('T')[0];
};

const fromNow = (date: Date | string): string => {
  // Get timestamps
  const unixTime = new Date(date).getTime();
  if (!unixTime) {
    return NOT_DEFINED_DATE;
  }
  const now = new Date().getTime();

  // Calculate difference
  let difference = unixTime / 1000 - now / 1000;

  // Check if time is in the past, present, or future
  let when = 'now';
  if (difference > 0) {
    when = 'futur';
  } else if (difference < -1) {
    when = 'past';
  }

  // Convert difference to absolute
  difference = Math.abs(difference);

  let unitOfTime = 'second';
  let time = Math.round(difference);
  // Calculate time unit
  if (difference / YEAR > 1) {
    // Years
    unitOfTime = 'year';
    time = Math.round(difference / YEAR);
  } else if (difference / MONTH > 1) {
    // Months
    unitOfTime = 'month';
    time = Math.round(difference / MONTH);
  } else if (difference / DAY > 1) {
    // Days
    unitOfTime = 'day';
    time = Math.round(difference / DAY);
  } else if (difference / HOUR > 1) {
    // Hours
    unitOfTime = 'hour';
    time = Math.round(difference / HOUR);
  }

  switch (when) {
    case 'futur':
      return new Intl.RelativeTimeFormat('en').format(time, unitOfTime as Intl.RelativeTimeFormatUnit);
    case 'past':
      return new Intl.RelativeTimeFormat('en').format(-time, unitOfTime as Intl.RelativeTimeFormatUnit);
    default:
      return getFormattedDate(date).full();
  }
};

export const dateSort = (a: IApplicationFull, b: IApplicationFull) =>
  new Date(b.recordDate).getTime() - new Date(a.recordDate).getTime();

export const getEndOfMonth = (date: string | Date): Date => {
  const newDate = new Date(date);

  newDate.setMonth(newDate.getMonth() + 1);
  newDate.setDate(0);
  newDate.setHours(0);

  return newDate;
};

// MORE FUNCTIONAL
export const getMaxAvailableDates = (availableDates: string[]): [string, string] => {
  const hasManyDates = availableDates.length > 1;

  const minDate = hasManyDates ? availableDates.shift()! : availableDates[0];
  const maxDate = hasManyDates ? availableDates.pop()! : availableDates[0];

  return [minDate, maxDate];
};

export const getRecords = (periodView: string, availableDates: string[]): [string, string] => {
  const lastMonth = availableDates.length > 1 ? availableDates[availableDates.length - 1] : availableDates[0];

  const lastThreeMonth = availableDates.length > 2 ? availableDates.slice(-3) : undefined;

  switch (periodView) {
    case 'ALL':
      return [availableDates[0], lastMonth];
    case 'LAST_THREE_MONTHS':
      if (typeof lastThreeMonth === 'undefined') {
        return [availableDates[0], lastMonth];
      }
      return [lastThreeMonth[0], lastThreeMonth.slice(-1).join()];
    default:
      return [availableDates[0], lastMonth];
  }
};

export const getTargettedRecords = <T>(periodView: string, applications: T[]): T[] => {
  const lastThreeMonth = applications.length > 2 ? applications.slice(-3) : applications;
  switch (periodView) {
    case 'ALL':
      return applications;
    case 'LAST_THREE_MONTHS':
      return lastThreeMonth;
    default:
      return lastThreeMonth;
  }
};

export const getRecordsCertification = (
  periodView: string,
  availableDates: string[],
  lastApptioRecordMonth: string
): [string, string] => {
  const periodDates = getRecords(periodView, availableDates);
  const lastApptioRecordMonthIndex = availableDates.findIndex(
    (value) => getFunctionnalDate(value).short() === getFunctionnalDate(lastApptioRecordMonth).short()
  );

  const fromIndex = availableDates.findIndex((value) => value === periodDates[0]);
  const toIndex = availableDates.findIndex((value) => value === periodDates[1]);

  const gap = toIndex - lastApptioRecordMonthIndex;

  const from = availableDates[fromIndex - gap > 0 ? fromIndex - gap : fromIndex];
  const to = availableDates[toIndex - gap > 0 ? toIndex - gap : toIndex];

  return [from, to];
};
